import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { X, Info } from "lucide-react";
import toast from "react-hot-toast";
import CustomDropdown from "../common/CustomDropdown";
import Toggle from "../common/Toggle";
import InfoTip from "../club-network/InfoTip";
import FeeFrameSelect from "./FeeFrameSelect";
import { scoutRequestService } from "../../services/scoutRequestService";
import { SCOUT_POSITIONS, SCOUT_CATEGORIES, SCOUT_FEET } from "../../constants/scoutRequestChoices";

// Create / edit a scout request (design 1a, final consolidated form; supersedes
// 2a). 720px modal. All copy verbatim from the design (English). The fee frame
// (7a) is guidance, never blocking. On save the resolved fee-frame payload is
// spread into the request body; the server owns the club + internal attribution.

const SegmentedControl = ({ options, value, onChange, ariaLabel }) => (
  <div className="inline-flex rounded-lg bg-[#F3F4F6] p-0.5" role="group" aria-label={ariaLabel}>
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={active}
          className={`px-3.5 py-1.5 rounded-md text-[13px] font-semibold transition ${
            active ? "bg-white text-[#111827] shadow-sm" : "text-[#6B7280] hover:text-[#111827]"
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const Field = ({ label, hint, tip, children, optional }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1.5">
      <label className="text-[13px] font-semibold text-[#111827]">{label}</label>
      {optional && <span className="text-[12px] text-[#9CA3AF]">(optional)</span>}
      {tip && <InfoTip text={tip} />}
    </div>
    {children}
    {hint && <p className="mt-1 text-[11.5px] text-[#9CA3AF] leading-snug">{hint}</p>}
  </div>
);

const inputCls =
  "w-full px-3.5 py-2.5 rounded-lg bg-[#F3F4F6] text-[14px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25 border border-transparent focus:border-[#2283FF]";

// Form body — mounted ONLY while the modal is open (see the wrapper at the
// bottom). The useState initialisers below run on mount, so keeping the body
// unmounted while closed is what makes Edit prefill work: the parent keeps the
// modal element in the tree from page load with initial=null, and a component
// that merely returned null after the hooks would seed empty state once and
// never re-read `initial` (the empty-edit / silent-wipe bug, same P0 as the
// agent-request fix in FE #157).
const ScoutRequestFormBody = ({ mode = "create", initial = null, onClose, onSaved }) => {
  const authUser = useSelector((state) => state.auth?.user);
  const currentRole = useSelector((state) => state.auth?.currentRole);

  const [category, setCategory] = useState(initial?.category || "professional");
  const [positionPrimary, setPositionPrimary] = useState(initial?.position_primary || "");
  const [positionSecondary, setPositionSecondary] = useState(initial?.position_secondary || "");
  const [ageMin, setAgeMin] = useState(initial?.age_min ?? "");
  const [ageMax, setAgeMax] = useState(initial?.age_max ?? "");
  const [preferredFoot, setPreferredFoot] = useState(initial?.preferred_foot || "");
  const [minHeight, setMinHeight] = useState(initial?.min_height_cm ?? "");
  const [horizon, setHorizon] = useState(initial?.horizon || "");
  const [levelExpectation, setLevelExpectation] = useState(initial?.level_expectation || "");
  const [feeFrame, setFeeFrame] = useState(
    initial
      ? {
          fee_frame_kind: initial.fee_frame?.kind || "open",
          fee_frame_currency: initial.fee_frame?.currency || "",
          fee_frame_min: initial.fee_frame?.min ?? null,
          fee_frame_max: initial.fee_frame?.max ?? null,
        }
      : { fee_frame_kind: "open" },
  );
  const [loanPossible, setLoanPossible] = useState(initial?.loan_possible || false);
  const [contextLine, setContextLine] = useState(initial?.context_line || "");
  const [saving, setSaving] = useState(false);

  const clubName = authUser?.club_name || authUser?.club?.name || authUser?.clubName || "your club";
  const posterName = authUser?.name || authUser?.full_name || "you";
  const roleLabel = useMemo(() => {
    const map = { ClubAdmin: "Club admin", Staff: "Staff", Coach: "Coach" };
    return map[currentRole] || currentRole || "Member";
  }, [currentRole]);

  // "Left" segmented option adds the Preferred-foot control; Either = no filter.
  const footOptions = [...SCOUT_FEET];

  const secondaryOptions = [{ value: "", label: "None" }, ...SCOUT_POSITIONS];

  const submit = async () => {
    if (!positionPrimary) return toast.error("Pick a primary position.");
    if (!horizon.trim()) return toast.error("Add a horizon, e.g. Summer 2027.");
    if (ageMin !== "" && ageMax !== "" && Number(ageMin) > Number(ageMax)) {
      return toast.error("Max age must be at least the min age.");
    }
    const payload = {
      category,
      position_primary: positionPrimary,
      position_secondary: positionSecondary || null,
      age_min: ageMin === "" ? null : Number(ageMin),
      age_max: ageMax === "" ? null : Number(ageMax),
      preferred_foot: preferredFoot || "",
      min_height_cm: minHeight === "" ? null : Number(minHeight),
      horizon: horizon.trim(),
      level_expectation: levelExpectation.trim(),
      loan_possible: loanPossible,
      context_line: contextLine.trim(),
      ...feeFrame,
    };
    setSaving(true);
    const res =
      mode === "edit"
        ? await scoutRequestService.updateRequest(initial.id, payload)
        : await scoutRequestService.createRequest(payload);
    setSaving(false);
    if (res.success) {
      toast.success(mode === "edit" ? "Scout request updated." : "Scout request posted.");
      onSaved?.(res.data);
      onClose?.();
    } else {
      toast.error(res.error || "Could not save the scout request.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/45 p-0 sm:p-4 overflow-y-auto">
      <div className="w-full sm:max-w-[720px] bg-white sm:rounded-2xl shadow-xl my-0 sm:my-6 flex flex-col max-h-screen sm:max-h-[92vh]">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-[#E5E7EB]">
          <div>
            <h2 className="text-[18px] font-bold text-[#111827]">
              {mode === "edit" ? "Edit scout request" : "New scout request"}
            </h2>
            <p className="text-[13px] text-[#6B7280] mt-0.5">
              Tell agents what you&apos;re watching for. No commitment attached.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-[#9CA3AF] hover:text-[#111827]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex flex-col gap-5">
          {/* Posting-as attribution card */}
          <div className="rounded-xl bg-[#F8FAFC] border border-[#E5E7EB] px-4 py-3">
            <p className="text-[13.5px] font-semibold text-[#111827]">Posting as {clubName}</p>
            <p className="text-[12px] text-[#6B7280] mt-0.5">
              Attributed internally to {posterName} · {roleLabel}
            </p>
          </div>

          {/* Category */}
          <Field label="Category">
            <SegmentedControl
              options={SCOUT_CATEGORIES}
              value={category}
              onChange={setCategory}
              ariaLabel="Category"
            />
          </Field>

          {/* Position + Secondary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Position">
              <CustomDropdown
                options={SCOUT_POSITIONS}
                value={positionPrimary}
                onChange={setPositionPrimary}
                placeholder="Select a position"
                displayProperty="label"
                valueProperty="value"
                searchEnabled
                className="!bg-[#F3F4F6] !border-transparent !text-[14px]"
                paddingY="py-2.5"
              />
            </Field>
            <Field label="Secondary position" optional>
              <CustomDropdown
                options={secondaryOptions}
                value={positionSecondary}
                onChange={setPositionSecondary}
                placeholder="None"
                displayProperty="label"
                valueProperty="value"
                searchEnabled
                className="!bg-[#F3F4F6] !border-transparent !text-[14px]"
                paddingY="py-2.5"
              />
            </Field>
          </div>

          {/* Age range */}
          <Field label="Age range">
            <div className="flex items-center gap-3 max-w-[240px]">
              <input
                type="number"
                min="0"
                value={ageMin}
                onChange={(e) => setAgeMin(e.target.value)}
                placeholder="17"
                className={inputCls}
                aria-label="Minimum age"
              />
              <span className="text-[#9CA3AF]">–</span>
              <input
                type="number"
                min="0"
                value={ageMax}
                onChange={(e) => setAgeMax(e.target.value)}
                placeholder="21"
                className={inputCls}
                aria-label="Maximum age"
              />
            </div>
          </Field>

          {/* Player profile */}
          <div className="rounded-xl border border-[#E5E7EB] px-4 py-4">
            <div className="flex items-center gap-1.5 mb-1">
              <h3 className="text-[13.5px] font-bold text-[#111827]">Player profile</h3>
              <span className="text-[12px] text-[#9CA3AF]">(optional)</span>
            </div>
            <p className="text-[11.5px] text-[#9CA3AF] mb-3">
              Only what genuinely matters. Every field narrows what agents send.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Preferred foot">
                <SegmentedControl
                  options={footOptions}
                  value={preferredFoot}
                  onChange={(v) => setPreferredFoot(v === preferredFoot ? "" : v)}
                  ariaLabel="Preferred foot"
                />
              </Field>
              <Field label="Minimum height">
                <div className="flex items-center gap-2 max-w-[200px]">
                  <input
                    type="number"
                    min="100"
                    max="250"
                    value={minHeight}
                    onChange={(e) => setMinHeight(e.target.value)}
                    placeholder="185"
                    className={inputCls}
                    aria-label="Minimum height in cm"
                  />
                  <span className="text-[13px] text-[#6B7280]">cm or taller</span>
                </div>
              </Field>
            </div>
          </div>

          {/* Horizon + Level */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Horizon"
              hint={'When this could get real: "next 12-18 months" works too.'}
            >
              <input
                type="text"
                value={horizon}
                onChange={(e) => setHorizon(e.target.value)}
                placeholder="Summer 2027"
                maxLength={80}
                className={inputCls}
              />
            </Field>
            <Field label="Level expectation">
              <input
                type="text"
                value={levelExpectation}
                onChange={(e) => setLevelExpectation(e.target.value)}
                placeholder="Super League or strong second tier"
                maxLength={120}
                className={inputCls}
              />
            </Field>
          </div>

          {/* Realistic frame */}
          <div className="rounded-xl border border-[#E5E7EB] px-4 py-4">
            <div className="flex items-center gap-1.5">
              <h3 className="text-[13.5px] font-bold text-[#111827]">Realistic frame</h3>
            </div>
            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5 mb-3">
              Agents see this: it keeps recommendations gettable, not a negotiation.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <Field label="Fee frame">
                <FeeFrameSelect value={feeFrame} onChange={setFeeFrame} />
              </Field>
              <Field label="Loan">
                <label className="flex items-center gap-2.5 mt-1 cursor-pointer">
                  <Toggle checked={loanPossible} onChange={setLoanPossible} label="Loan is an option" />
                  <span className="text-[13px] text-[#374151]">Loan is an option</span>
                </label>
              </Field>
            </div>
          </div>

          {/* Free text */}
          <Field label="What are we watching for?">
            <textarea
              value={contextLine}
              onChange={(e) => setContextLine(e.target.value)}
              rows={2}
              maxLength={200}
              placeholder="Left-footed preferred, comfortable building from the back."
              className={`${inputCls} resize-none`}
            />
          </Field>

          {/* Public-name info banner */}
          <div className="rounded-xl bg-[#E8F1FF] border border-[#BFDBFE] px-4 py-3 flex gap-2.5">
            <Info className="w-4 h-4 text-[#1B6FE0] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-semibold text-[#1B6FE0]">
                Posted publicly under your club&apos;s name.
              </p>
              <p className="text-[12px] text-[#1B6FE0]/80 mt-0.5">
                Agents see it and recommend players. Recommendations are visible only to you.
              </p>
            </div>
          </div>
        </div>

        {/* Footer — full-width sticky CTAs on mobile (≥44px), right-aligned on desktop */}
        <div className="flex items-center justify-end gap-3 px-4 sm:px-6 py-4 border-t border-[#E5E7EB]">
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none min-h-[44px] px-4 py-2 rounded-lg border border-[#E5E7EB] text-[13px] font-semibold text-[#111827] hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 sm:flex-none min-h-[44px] px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0] disabled:opacity-50 whitespace-nowrap"
          >
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Post scout request"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Public modal: renders nothing while closed so the body (and its useState
// seeds) mounts fresh on every open. The key remounts the body if a consumer
// swaps `initial` (or switches create↔edit) without closing in between.
const ScoutRequestFormModal = ({ open, mode = "create", initial = null, onClose, onSaved }) => {
  if (!open) return null;
  return (
    <ScoutRequestFormBody
      key={initial?.id ?? "create"}
      mode={mode}
      initial={initial}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
};

export default ScoutRequestFormModal;
